import { useEffect, useRef } from "react";
import { glowId, normalizeStateName } from "../data/utils";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string;
const MAPBOX_STYLE = "mapbox://styles/inaris/cmms6ocx8008c01qsga3h03yy";
const NONE_NAME = "__none__";

type GlowSpec = {
  suffix: string;
  width: number;
  blur: number;
  opacity: number;
  color: string;
};

const HOVER_STACK: GlowSpec[] = [
  { suffix: "halo-far", width: 24, blur: 20, opacity: 0.35, color: "#00B3FF" },
  { suffix: "halo-mid", width: 14, blur: 10, opacity: 0.6, color: "#33C8FF" },
  { suffix: "halo-near", width: 7, blur: 4, opacity: 0.85, color: "#66D9FF" },
  { suffix: "glow", width: 3, blur: 1, opacity: 1.0, color: "#B3ECFF" },
  { suffix: "core", width: 1.5, blur: 0, opacity: 1.0, color: "#FFFFFF" },
];

const SELECTED_STACK: GlowSpec[] = [
  { suffix: "halo-far", width: 24, blur: 20, opacity: 0.35, color: "#9000FF" },
  { suffix: "halo-mid", width: 14, blur: 10, opacity: 0.6, color: "#A833FF" },
  { suffix: "halo-near", width: 7, blur: 4, opacity: 0.85, color: "#C066FF" },
  { suffix: "glow", width: 3, blur: 1, opacity: 1.0, color: "#E5C2FF" },
  { suffix: "core", width: 1.5, blur: 0, opacity: 1.0, color: "#FFFFFF" },
];

const HOVER_LAYERS = HOVER_STACK.map((s) => `state-hover-${s.suffix}`);
const SELECTED_LAYERS = SELECTED_STACK.map((s) => `state-selected-${s.suffix}`);

type GlobeMapHandle = {
  map: any;
  flyToState: (name: string) => void;
};

type Props = {
  visible: boolean;
  selectedStateName: string | null;
  onStateClick: (name: string | null) => void;
  onMapReady?: (handle: GlobeMapHandle) => void;
};

// ---------- Geometry helpers ----------

// Signed area of a planar ring (shoelace). We only use magnitude.
function ringArea(ring: number[][]): number {
  let area = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return area / 2;
}

// Ray-cast point-in-polygon test against an outer ring.
function pointInRing(px: number, py: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    if (
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

// Squared distance from a point to a line segment.
function segDistSq(
  px: number,
  py: number,
  a: number[],
  b: number[]
): number {
  let x = a[0];
  let y = a[1];
  let dx = b[0] - x;
  let dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      x = b[0];
      y = b[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }
  dx = px - x;
  dy = py - y;
  return dx * dx + dy * dy;
}

// Minimum distance from a point to the polygon's border (any edge).
function distToBorderSq(px: number, py: number, ring: number[][]): number {
  let min = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const d = segDistSq(px, py, ring[j], ring[i]);
    if (d < min) min = d;
  }
  return min;
}

// Pole of inaccessibility: the point inside the ring that is furthest from
// any border edge. Visually centered for almost any state shape — handles
// concave outlines (Florida, Louisiana, Idaho) far better than a centroid.
// Uses a coarse grid + local refinement; cheap enough to run at startup.
function poleOfInaccessibility(ring: number[][]): [number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const w = maxX - minX;
  const h = maxY - minY;

  let bestX = (minX + maxX) / 2;
  let bestY = (minY + maxY) / 2;
  let bestDist = -Infinity;

  const COARSE = 32;
  for (let i = 0; i < COARSE; i++) {
    for (let j = 0; j < COARSE; j++) {
      const x = minX + ((i + 0.5) * w) / COARSE;
      const y = minY + ((j + 0.5) * h) / COARSE;
      if (!pointInRing(x, y, ring)) continue;
      const d = distToBorderSq(x, y, ring);
      if (d > bestDist) {
        bestDist = d;
        bestX = x;
        bestY = y;
      }
    }
  }

  // Local refinement around the best coarse cell.
  if (bestDist > 0) {
    const cellW = w / COARSE;
    const cellH = h / COARSE;
    const FINE = 10;
    for (let i = 0; i < FINE; i++) {
      for (let j = 0; j < FINE; j++) {
        const x = bestX - cellW + ((i + 0.5) * 2 * cellW) / FINE;
        const y = bestY - cellH + ((j + 0.5) * 2 * cellH) / FINE;
        if (!pointInRing(x, y, ring)) continue;
        const d = distToBorderSq(x, y, ring);
        if (d > bestDist) {
          bestDist = d;
          bestX = x;
          bestY = y;
        }
      }
    }
  }

  return [bestX, bestY];
}

// Build a point FeatureCollection with ONE feature per state, placed at the
// pole of inaccessibility of the state's LARGEST polygon piece. Skips
// MultiPolygon duplication (no Alaska-per-island) and stays visually
// centered even for concave / L-shaped states.
function buildLabelFeatureCollection(geojson: any): any {
  const features: any[] = [];
  for (const f of geojson.features || []) {
    const name: string | null = f.properties?.name ?? null;
    if (!name || !f.geometry) continue;

    const g = f.geometry;
    let largest = 0;
    let chosenRing: number[][] | null = null;

    if (g.type === "Polygon") {
      const outer = g.coordinates[0];
      const a = Math.abs(ringArea(outer));
      if (a > largest) {
        largest = a;
        chosenRing = outer;
      }
    } else if (g.type === "MultiPolygon") {
      for (const poly of g.coordinates) {
        const outer = poly[0];
        const a = Math.abs(ringArea(outer));
        if (a > largest) {
          largest = a;
          chosenRing = outer;
        }
      }
    }

    if (chosenRing) {
      const pt = poleOfInaccessibility(chosenRing);
      features.push({
        type: "Feature",
        properties: { name },
        geometry: { type: "Point", coordinates: pt },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

// ---------- Component ----------

export function GlobeMap({
  visible,
  selectedStateName,
  onStateClick,
  onMapReady,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const featureMapRef = useRef<Map<string, any>>(new Map());
  const hoverNameRef = useRef<string | null>(null);
  const selectedNameRef = useRef<string | null>(selectedStateName);
  const layersReadyRef = useRef(false);

  useEffect(() => {
    selectedNameRef.current = selectedStateName;
  }, [selectedStateName]);

  function setHoverName(name: string | null) {
    const map = mapRef.current;
    if (!map || !layersReadyRef.current) return;
    const sel = selectedNameRef.current;
    const target = name && name !== sel ? name : NONE_NAME;
    for (const id of HOVER_LAYERS) {
      try {
        map.setFilter(id, ["==", ["get", "name"], target]);
      } catch {
        /* layer not ready */
      }
    }
  }

  function setSelectedName(name: string | null) {
    const map = mapRef.current;
    if (!map || !layersReadyRef.current) return;
    const target = name ?? NONE_NAME;
    for (const id of SELECTED_LAYERS) {
      try {
        map.setFilter(id, ["==", ["get", "name"], target]);
      } catch {
        /* layer not ready */
      }
    }
  }

  useEffect(() => {
    setSelectedName(selectedStateName);
    setHoverName(hoverNameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStateName]);

  function flyToState(name: string) {
    const map = mapRef.current;
    if (!map) return;
    const id = glowId(name);
    const feature = featureMapRef.current.get(id);
    if (!feature) return;
    const bounds = computeBounds(feature);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: 120, maxZoom: 6, duration: 1200 });
  }

  function computeBounds(
    feature: any
  ): [[number, number], [number, number]] | null {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const visit = (coords: any) => {
      if (typeof coords[0] === "number") {
        const [x, y] = coords;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      } else {
        for (const c of coords) visit(c);
      }
    };
    visit(feature.geometry.coordinates);
    if (!isFinite(minX)) return null;
    return [
      [minX, minY],
      [maxX, maxY],
    ];
  }

  useEffect(() => {
    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) {
      console.error("Mapbox GL JS not loaded from CDN");
      return;
    }
    if (!MAPBOX_TOKEN) {
      console.error("VITE_MAPBOX_TOKEN missing");
      return;
    }
    if (!containerRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLE,
      projection: "globe",
      zoom: 3.5,
      center: [-98.5, 39.5],
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", async () => {
      try {
        map.setFog({});
      } catch {
        /* noop */
      }

      try {
        const res = await fetch("/data/us-states.geojson");
        const geojson = await res.json();

        for (const f of geojson.features || []) {
          const rawName: string | null = f.properties?.name ?? null;
          const normalized = normalizeStateName(rawName);
          if (!normalized) continue;
          f.properties.name = normalized;
          featureMapRef.current.set(glowId(normalized), f);
        }

        // Polygon source for boundaries, fills, glow.
        map.addSource("us-states", { type: "geojson", data: geojson });

        // One-point-per-state source for clean labels (no duplicates).
        const labelFC = buildLabelFeatureCollection(geojson);
        map.addSource("us-state-labels", { type: "geojson", data: labelFC });

        // 1. White state boundaries (always visible)
        map.addLayer({
          id: "state-boundaries",
          type: "line",
          source: "us-states",
          paint: {
            "line-color": "#FFFFFF",
            "line-width": 1.0,
            "line-opacity": 0.7,
          },
        });

        // 2. Selected glow stack
        for (const spec of SELECTED_STACK) {
          map.addLayer({
            id: `state-selected-${spec.suffix}`,
            type: "line",
            source: "us-states",
            filter: ["==", ["get", "name"], NONE_NAME],
            paint: {
              "line-color": spec.color,
              "line-width": spec.width,
              "line-blur": spec.blur,
              "line-opacity": spec.opacity,
              "line-join": "round",
              "line-cap": "round",
            },
          });
        }

        // 3. Hover glow stack
        for (const spec of HOVER_STACK) {
          map.addLayer({
            id: `state-hover-${spec.suffix}`,
            type: "line",
            source: "us-states",
            filter: ["==", ["get", "name"], NONE_NAME],
            paint: {
              "line-color": spec.color,
              "line-width": spec.width,
              "line-blur": spec.blur,
              "line-opacity": spec.opacity,
              "line-join": "round",
              "line-cap": "round",
            },
          });
        }

        // 4. Invisible hit-testing fill
        map.addLayer({
          id: "state-fill",
          type: "fill",
          source: "us-states",
          paint: { "fill-color": "#FFFFFF", "fill-opacity": 0 },
        });

        // 5. Labels — one per state from the point FC.
        const baseLabelLayer: any = {
          id: "state-labels",
          type: "symbol",
          source: "us-state-labels",
          layout: {
            "text-field": ["get", "name"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              2,
              9,
              3.5,
              11,
              5,
              14,
              7,
              18,
            ],
            "text-letter-spacing": 0.06,
            "text-transform": "uppercase",
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            "text-padding": 2,
            "symbol-placement": "point",
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "rgba(0,0,0,0.75)",
            "text-halo-width": 1.4,
            "text-halo-blur": 0.4,
          },
        };
        try {
          map.addLayer({
            ...baseLabelLayer,
            layout: {
              ...baseLabelLayer.layout,
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
            },
          });
        } catch {
          try {
            map.addLayer(baseLabelLayer);
          } catch {
            /* noop */
          }
        }

        layersReadyRef.current = true;

        map.on("mousemove", "state-fill", (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          map.getCanvas().style.cursor = "pointer";
          const name = normalizeStateName(f.properties?.name ?? null);
          if (name && name !== hoverNameRef.current) {
            hoverNameRef.current = name;
            setHoverName(name);
          }
        });

        map.on("mouseleave", "state-fill", () => {
          map.getCanvas().style.cursor = "";
          hoverNameRef.current = null;
          setHoverName(null);
        });

        map.on("click", "state-fill", (e: any) => {
          const f = e.features?.[0];
          const name = normalizeStateName(f?.properties?.name ?? null);
          if (name) onStateClick(name);
        });

        map.on("click", (e: any) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ["state-fill"],
          });
          if (!features || features.length === 0) onStateClick(null);
        });

        if (selectedNameRef.current) setSelectedName(selectedNameRef.current);

        const handle: GlobeMapHandle = { map, flyToState };
        onMapReady?.(handle);
      } catch (err) {
        console.error("Failed to load us-states.geojson:", err);
      }
    });

    return () => {
      try {
        map.remove();
      } catch {
        /* noop */
      }
      mapRef.current = null;
      layersReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.visibility = visible ? "visible" : "hidden";
    if (visible && mapRef.current) {
      requestAnimationFrame(() => {
        try {
          mapRef.current.resize();
        } catch {
          /* noop */
        }
      });
    }
  }, [visible]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
      }}
    />
  );
}

export type { GlobeMapHandle };
